import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { generateEncodedMessage } from '../utils/baconianCipher';
import { getRandomQuote } from '../utils/cipherUtils';

const BaconianPractice = () => {
  const navigate = useNavigate();
  const [currentMessage, setCurrentMessage] = useState('');
  const [encodedGroups, setEncodedGroups] = useState([]);
  const [userInputs, setUserInputs] = useState({});
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchQuotes();
    const timer = setInterval(() => {
      if (isTimerRunning) {
        setTimeElapsed(prev => prev + 1);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [isTimerRunning]);

  const fetchQuotes = async () => {
    setIsLoading(true);
    try {
      let quote = null;
      for (let i = 0; i < 10; i++) {
        const candidate = await getRandomQuote();
        if (candidate && candidate.text && candidate.text.split(/\s+/).length <= 20) {
          quote = candidate;
          break;
        }
      }
      if (quote) {
        const cleanText = quote.text.replace(/[^a-zA-Z\s]/g, '');
        setCurrentMessage(cleanText);
        const { encodedGroups } = generateEncodedMessage(cleanText);
        setEncodedGroups(encodedGroups);
        setUserInputs({});
        setTimeElapsed(0);
        setIsTimerRunning(true);
        setMessage({ type: '', text: '' });
      } else {
        setMessage({ type: 'error', text: 'No valid quote found (less than 20 words).' });
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setMessage({ type: 'error', text: 'Error loading quotes. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (index, value) => {
    const newValue = value.toUpperCase().replace(/[^A-Z]/g, '');
    setUserInputs(prev => ({
      ...prev,
      [index]: newValue
    }));
  };

  const checkSolution = () => {
    const userAnswer = Object.values(userInputs).join('').replace(/\s/g, '');
    const correctAnswer = currentMessage.replace(/[^A-Za-z]/g, '').toUpperCase();
    
    if (userAnswer === correctAnswer) {
      setMessage({ type: 'success', text: 'Correct! Moving to next puzzle...' });
      setIsTimerRunning(false);
      setTimeout(() => {
        fetchQuotes();
      }, 1500);
    } else {
      setMessage({ type: 'error', text: 'Not quite right. Try again!' });
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          Baconian Cipher Practice
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Time Elapsed">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimerIcon />
              <Typography>{formatTime(timeElapsed)}</Typography>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 2,
          justifyContent: 'center',
          p: 2,
          bgcolor: 'grey.100',
          borderRadius: 1,
        }}>
          {encodedGroups.map((group, index) => (
            <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontFamily: 'monospace',
                  mb: 1,
                  p: 1,
                  bgcolor: 'white',
                  borderRadius: 1,
                  minWidth: '60px',
                  textAlign: 'center'
                }}
              >
                {Array.isArray(group.display)
                  ? group.display.map((char, i) =>
                      typeof char === 'object'
                        ? <span key={i} style={char.italic ? { fontStyle: 'italic' } : {}}>{char.char}</span>
                        : <span key={i} style={{ marginRight: 4 }}>{char}</span>
                    )
                  : group.display}
              </Typography>
              {group.code !== ' ' && (
                <TextField
                  size="small"
                  inputProps={{ 
                    maxLength: 1,
                    style: { textAlign: 'center', textTransform: 'uppercase' }
                  }}
                  value={userInputs[index] || ''}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  sx={{
                    width: '40px',
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                      textTransform: 'uppercase'
                    }
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={checkSolution}
            size="large"
          >
            Check Answer
          </Button>
        </Box>

        {message.text && (
          <Alert severity={message.type} sx={{ mt: 2 }}>
            {message.text}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default BaconianPractice; 